import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import React, {useEffect, useState} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {AccountItems, AccountType, AccountUserBase, TwoFactorOverride, UserAccessLevel} from "@/lib/types/accounts.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactGroupBaseItems} from "@/lib/types/contacts.ts";
import EditUserFormBasic, {EditUserInfoSchema, UserFormValues} from "@/pages/accounts/edit/EditUserFormBasic.tsx";
import {AccountUserTabs} from "@/pages/accounts/edit/EditUserFormTypes.ts";

interface EditUserFormProps {
  onSubmit: (data: UserFormValues) => Promise<void>;
  user?: AccountUserBase;
  accountAccounts: AccountItems;
  availableContactGroups: ContactGroupBaseItems;
  formId?: string;
  disabled?: boolean;
  serverErrors?: ServerErrors;
  onError?: (errors: FieldErrors<UserFormValues>, event?: React.BaseSyntheticEvent) => void;
  activeTab?: AccountUserTabs;
}

export const EditUserForm = ({
  formId,
  onSubmit,
  onError,
  user,
  accountAccounts,
  availableContactGroups,
  serverErrors,
  activeTab,
}: EditUserFormProps) => {
  const [tab, setTab] = useState(activeTab ?? AccountUserTabs.BASIC_INFO);
  const methods = useForm<UserFormValues>({
    resolver: yupResolver<UserFormValues>(EditUserInfoSchema),
    defaultValues: {
      id: user?.id,
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
      access_level: user?.access_level ?? UserAccessLevel.READ,
      is_api_enabled: user?.is_api_enabled ?? true,
      is_mobile_enabled: user?.is_mobile_enabled ?? false,
      has_purchase_permission: user?.has_purchase_permission ?? false,
      require_two_factor: user?.require_two_factor ?? TwoFactorOverride.ACCOUNT_DEFAULT,
      notify_paid_invoices: user?.notify_paid_invoices ?? false,
      notify_important_updates: user?.notify_important_updates ?? false,
      contact_groups: [], // pass contact groups and select first one
      assigned_subaccounts: user?.assigned_subaccounts.map((subaccount) => subaccount.id) || [],
    },
  });
  const {user: currentUser} = useCurrentUserContext();
  useEffect(() => {
    if (!serverErrors) {
      return;
    }
    if (serverErrors.schedule_entries || serverErrors.schedule_state) {
      setTab(AccountUserTabs.BASIC_INFO);
    }
  }, [setTab, serverErrors]);

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTab(AccountUserTabs.BASIC_INFO);
          methods.handleSubmit(onSubmit, onError)(e);
        }}
        noValidate
      >
        <EditUserFormBasic
          className={classNames({"d-none": tab !== AccountUserTabs.BASIC_INFO})}
          serverErrors={serverErrors}
          accountAccounts={accountAccounts}
          availableContactGroups={availableContactGroups}
          accountHasPurchasePermission={currentUser!.account.has_can_purchase_feature}
          isRegularAccount={currentUser!.account.type === AccountType.REGULAR}
          isEditingPrimaryUser={user?.is_primary ?? false}
        />
      </form>
    </FormProvider>
  );
};
